import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { downloadFile, extractFilename } from '@seu-helpers';
import { LanguageService } from '@seu-services';
import { catchError, Observable, throwError } from 'rxjs';
import {
  ExportOfferRequestParams,
  OfferComputationApiService,
  SeuApiOfferComputation,
} from 'src/app/shared/api';
import { ToastService } from './toast.service';

export type SeuExportType = ExportOfferRequestParams['exportType'];

export type SeuExportParams = {
  offerId: string;
  computations: SeuApiOfferComputation[];
};

@Injectable({
  providedIn: 'root',
})
export class ExportService {
  private readonly offerComputationApiService = inject(
    OfferComputationApiService
  );
  private readonly translateService = inject(TranslateService);
  private readonly toastService = inject(ToastService);
  private readonly languageService = inject(LanguageService);

  /**
   * Exports a calculation result as Excel format
   *
   * @param params - Export parameters including offerId and calculation identifiers
   * @returns Observable that completes when export is finished
   */
  public exportExcel(params: SeuExportParams): Observable<void> {
    return this.exportFile(params, 'EXCEL', 'xlsx');
  }

  /**
   * Exports a calculation result as EB_IMPORT format
   *
   * @param params - Export parameters including offerId and calculation identifiers
   * @returns Observable that completes when export is finished
   */
  public exportEBImport(params: SeuExportParams): Observable<void> {
    return this.exportFile(params, 'EB_IMPORT', 'xlsx');
  }

  /**
   * Internal method to handle the export process for any format
   *
   * @param params - Export parameters
   * @param exportType - The type of export (CSV, EXCEL, EB_IMPORT)
   * @param defaultExtension - Default file extension if filename extraction fails
   * @returns Observable that completes when export is finished
   */
  private exportFile(
    params: SeuExportParams,
    exportType: SeuExportType,
    defaultExtension: string
  ): Observable<void> {
    const { offerId } = params;

    const currentLang = this.languageService.getLang();

    // Call API with observe: 'response' to access headers
    return this.offerComputationApiService
      .exportOffer(
        {
          offerId,
          exportType,
          seuApiOfferComputation: params.computations,
          language: currentLang,
        },
        'response',
        false,
        {
          httpHeaderAccept: 'application/octet-stream',
        }
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error('Export failed:', error);
          this.showErrorToast();
          return throwError(() => error);
        })
      )
      .pipe(
        (observable) =>
          new Observable<void>((subscriber) => {
            observable.subscribe({
              next: (response) => {
                // Extract blob from response body
                const blob = response.body;
                if (!blob) {
                  this.showErrorToast();
                  subscriber.error(new Error('No blob in response'));
                  return;
                }

                // Extract filename from Content-Disposition header
                const contentDisposition = response.headers.get(
                  'Content-Disposition'
                );

                // Log warning if Content-Disposition header is not accessible
                // This usually indicates missing CORS Access-Control-Expose-Headers configuration
                if (!contentDisposition) {
                  console.warn(
                    'Content-Disposition header not accessible. Backend needs to expose it via Access-Control-Expose-Headers.'
                  );
                }

                let filename = extractFilename(contentDisposition);

                // Fallback filename if extraction fails
                if (!filename) {
                  const timestamp = new Date().toISOString().split('T')[0];
                  filename = `offer-${offerId}-${timestamp}.${defaultExtension}`;
                }

                // Trigger download
                downloadFile(blob, filename);
                subscriber.next();
                subscriber.complete();
              },
              error: (error) => subscriber.error(error),
            });
          })
      );
  }

  /**
   * Shows an error toast notification for export failures
   */
  private showErrorToast(): void {
    this.toastService.addToast({
      type: 'error',
      text: this.translateService.instant('export.error'),
    });
  }
}
